import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Pagination,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Chip,
} from "@mui/material";
import { apiService } from "../app/api";
import BaseLayout from "../layout/BaseLayout";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatTimestamp } from "../helper/formatTimestamp";
import { toast } from "react-toastify";
import MultiSelectDropdown from "../component/MultiSelectDropdown";
import { handleAxiosError } from "../helper/errorHandler";
import EditIcon from "@mui/icons-material/Edit";

interface AvatarItem {
  _id: string;
  avatar: string;
  is_active: boolean;
  supported_language: string[];
  createdAt: string;
}

const Avatar: React.FC = () => {
  const [avatars, setAvatars] = useState<AvatarItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);

  // Add Avatar Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAvatar, setNewAvatar] = useState("");
  const [newLanguages, setNewLanguages] = useState<string[]>([]);

  // Delete Confirmation Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Edit Avatar Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editAvatar, setEditAvatar] = useState("");
  const [editLanguages, setEditLanguages] = useState<string[]>([]);

  const fetchAvatars = async (pageNumber: number, searchQuery: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiService.get(
        `/avatar?page=${pageNumber}&limit=16&search=${searchQuery}`
      );
      setAvatars(data.data);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch avatars.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchAvatars(page, search);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [page, search]);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleAddAvatar = async () => {
    if (!newAvatar.trim()) {
      toast.warning("Please enter an avatar name.");
      return;
    }

    if (newLanguages.length === 0) {
      toast.warning("Please select at least one supported language.");
      return;
    }

    const payload = {
      avatar: newAvatar.trim().toLowerCase().replace(/\s+/g, "_"),
      supported_language: newLanguages, // plain array of strings
    };

    try {
      await apiService.post("/avatar", payload);
      setAddDialogOpen(false);
      setNewAvatar("");
      setNewLanguages([]);
      fetchAvatars(page, search);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to add avatar.");
    }
  };

  const handleDropdownChange = (value: string[]) => {
    if (value.includes("all")) {
      setNewLanguages(languages);
    } else {
      setNewLanguages(value);
    }
  };

  const fetchLanguages = async () => {
    try {
      const { data } = await apiService.get(
        "/languages?is_active=true&page=1&limit=100&fields=language"
      );
      const langArray = data.data.map((lang: any) => lang.language);
      setLanguages(langArray);
    } catch (error: any) {
      handleAxiosError(error);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!deleteId) return;
    try {
      await apiService.delete(`/avatar/${deleteId}`);
      setDeleteDialogOpen(false);
      fetchAvatars(page, search);
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  const handleEditClick = (avt: AvatarItem) => {
    setEditId(avt._id);
    setEditAvatar(avt.avatar);
    setEditLanguages(avt.supported_language || []);
    setEditDialogOpen(true);
  };

  const handleEditAvatar = async () => {
    if (editLanguages.length === 0) {
      toast.warning("Please select at least one supported language.");
      return;
    }

    const payload = {
      supported_language: editLanguages,
    };

    try {
      await apiService.put(`/avatar/${editId}`, payload);
      toast.success("Avatar updated successfully.");
      setEditDialogOpen(false);
      setEditId(null);
      fetchAvatars(page, search);
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  return (
    <BaseLayout>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
      >
        <Typography fontSize={30} fontWeight={700}>
          Avatar ({total})
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Search avatars..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: "400px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setAddDialogOpen(true)}
          >
            Add Avatar
          </Button>
        </Box>
      </Box>

      {/* Loader */}
      {loading && (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {avatars.length > 0 ? (
            <Grid container spacing={3} mt={3}>
              {avatars.map((avt) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={avt._id}>
                  <Card
                    sx={{
                      boxShadow: 3,
                      borderRadius: 2,
                      "&:hover": { boxShadow: 6 },
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" fontWeight={600}>
                        {avt.avatar}
                      </Typography>

                      {avt.createdAt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Added on: {formatTimestamp(avt.createdAt)}
                        </Typography>
                      )}

                      {/* Supported Languages */}
                      {avt.supported_language &&
                        avt.supported_language.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="body2" fontWeight={600}>
                              Supported Languages:
                            </Typography>
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              gap={0.5}
                              mt={0.5}
                            >
                              {avt.supported_language.map((langObj, idx) => (
                                <Chip key={idx} label={langObj} size="small" />
                              ))}
                            </Box>
                          </Box>
                        )}

                      <IconButton
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => {
                          setDeleteId(avt._id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>

                      <IconButton
                        sx={{ position: "absolute", top: 8, right: 40 }}
                        onClick={() => handleEditClick(avt)}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography textAlign="center" mt={5}>
              No avatars found.
            </Typography>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Add Avatar Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Avatar</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            Enter avatar name (no spaces; underscores will be used).
          </Typography>
          <TextField
            fullWidth
            value={newAvatar}
            onChange={(e) =>
              setNewAvatar(e.target.value.replace(/\s+/g, "_").toLowerCase())
            }
            placeholder="my_avatar"
            sx={{ mb: 2 }}
          />

          <MultiSelectDropdown
            options={languages.slice().sort((a, b) => a.localeCompare(b))}
            value={newLanguages}
            onChange={handleDropdownChange}
            label="Supported Language"
            placeholder="Type to search..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAvatar}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit  */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Avatar</DialogTitle>
        <DialogContent>
          <TextField fullWidth value={editAvatar} disabled sx={{ mb: 2 }} />

          <MultiSelectDropdown
            options={languages.slice().sort((a, b) => a.localeCompare(b))}
            value={editLanguages}
            onChange={(val) => {
              if (val.includes("all")) setEditLanguages(languages);
              else setEditLanguages(val);
            }}
            label="Supported Language"
            placeholder="Type to search..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditAvatar}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Avatar</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this avatar?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAvatar}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </BaseLayout>
  );
};

export default Avatar;
